import { useState } from "react";
import { Download, Eye, FileText, Loader2, ScrollText } from "lucide-react";

import {
  GraphAttachmentPdfDialog,
  type GraphAttachmentRef,
} from "@/components/common/GraphAttachmentPdfDialog";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import { Badge } from "@/components/ui/badge";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { blDocumentTypeBadge } from "@/features/bookings/bookingBlDocuments";

import type { BlDocumentSummary } from "./kaiToolDownloads";

function blAttachmentFilename(doc: BlDocumentSummary): string {
  return doc.filename ?? `${doc.blNumber ?? "bill-of-lading"}.pdf`;
}

/** True for files the in-app dialog can render inline (PDF / image). */
function isPreviewable(filename: string): boolean {
  return /\.(pdf|png|jpe?g|gif|webp|avif|bmp)$/i.test(filename);
}

/** Download button for a single parsed bill-of-lading PDF. */
function BlDownloadButton({ doc }: { doc: BlDocumentSummary }) {
  const [downloading, setDownloading] = useState(false);

  const canDownload =
    doc.downloadable &&
    !!doc.mailboxId &&
    !!doc.messageId &&
    !!doc.attachmentId;

  const handleClick = async () => {
    if (!canDownload || !doc.mailboxId || !doc.messageId || !doc.attachmentId) {
      return;
    }
    setDownloading(true);
    try {
      await api.downloadGraphAttachment(
        doc.mailboxId,
        doc.messageId,
        doc.attachmentId,
        blAttachmentFilename(doc),
      );
    } catch (err) {
      console.warn("Failed to download bill of lading:", err);
    } finally {
      setDownloading(false);
    }
  };

  if (!canDownload) {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/50 px-2.5 py-1 text-[11px] text-muted-foreground"
        title="The source PDF is no longer stored for this document."
      >
        Not available
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-foreground/[0.03] px-2.5 py-1 text-[11px] text-foreground/90 transition-colors hover:bg-accent/40",
        downloading && "opacity-60",
      )}
    >
      {downloading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Download className="size-3.5" />
      )}
      Download
    </button>
  );
}

/**
 * Compact card rendering the parsed bills of lading returned by
 * `api_get_booking_bl_documents`: one row per document with its kind badge, BL
 * number, lane, container count, received date, and a Download button for the
 * source PDF, and Preview for PDFs / images.
 */
export function KaiBlDocumentsCard({
  documents,
}: {
  documents: BlDocumentSummary[];
}) {
  const { formatDate } = useDateFormatter();
  const [preview, setPreview] = useState<GraphAttachmentRef | null>(null);

  if (documents.length === 0) return null;

  return (
    <div className="glass-panel my-1 flex flex-col gap-2 rounded-xl p-3">
      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <ScrollText className="size-4 shrink-0" />
        {documents.length === 1
          ? "Bill of lading"
          : `${documents.length} bills of lading`}
      </span>

      <div className="flex flex-col divide-y divide-border/40">
        {documents.map((doc) => {
          const filename = blAttachmentFilename(doc);
          const canDownload =
            doc.downloadable &&
            !!doc.mailboxId &&
            !!doc.messageId &&
            !!doc.attachmentId;
          const previewable = canDownload && isPreviewable(filename);
          const lane =
            doc.portOfLoading && doc.portOfDischarge
              ? `${doc.portOfLoading} → ${doc.portOfDischarge}`
              : (doc.portOfLoading ?? doc.portOfDischarge ?? null);
          return (
            <div
              key={doc.key}
              className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="secondary"
                    className="shrink-0 text-[10px] font-medium"
                  >
                    {blDocumentTypeBadge(doc.documentType)}
                  </Badge>
                  <span className="flex min-w-0 items-center gap-1 text-sm font-medium text-foreground">
                    <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate">
                      {doc.blNumber ?? doc.filename ?? "Bill of lading"}
                    </span>
                  </span>
                </div>
                <span className="truncate text-[11px] text-muted-foreground">
                  {[
                    lane,
                    doc.vessel,
                    doc.containerCount > 0
                      ? `${doc.containerCount} container${doc.containerCount === 1 ? "" : "s"}`
                      : null,
                    doc.receivedAt ? formatDate(doc.receivedAt) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {!canDownload ? (
                  <span
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/50 px-2.5 py-1 text-[11px] text-muted-foreground"
                    title="The source PDF is no longer stored for this document."
                  >
                    Not available
                  </span>
                ) : (
                  <>
                    {previewable && (
                      <button
                        type="button"
                        onClick={() =>
                          setPreview({
                            mailboxId: doc.mailboxId!,
                            messageId: doc.messageId!,
                            attachmentId: doc.attachmentId!,
                            filename,
                          })
                        }
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-foreground/[0.03] px-2.5 py-1 text-[11px] text-foreground/90 transition-colors hover:bg-accent/40"
                      >
                        <Eye className="size-3.5" />
                        Preview
                      </button>
                    )}
                    <BlDownloadButton doc={doc} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <GraphAttachmentPdfDialog
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        attachment={preview}
        previewDescription="Bill of lading preview"
      />
    </div>
  );
}
